import React from "react";
import { Helmet } from "react-helmet";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";

import Footer from "./Footer";
import Header from "./Header";
import { seoPageList, seoPages } from "../data/seoPages";

export default function SeoServicePage() {
  const { slug } = useParams();
  const page = seoPages[slug];

  if (!page) {
    return <Navigate to="/" replace />;
  }

  const canonicalUrl = `https://fold-web-agency.fr/${page.slug}`;
  const relatedPages = seoPageList.filter((entry) => entry.slug !== page.slug);
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
  const serviceStructuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.title,
    description: page.metaDescription,
    provider: {
      "@type": "Organization",
      name: "Fold Web Agency",
      url: "https://fold-web-agency.fr/",
    },
    areaServed: {
      "@type": "Country",
      name: "France",
    },
    url: canonicalUrl,
  };
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://fold-web-agency.fr/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: page.title,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      <Helmet>
        <title>{page.metaTitle}</title>
        <meta name="description" content={page.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={page.metaTitle} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      </Helmet>

      <Header />

      <main className="seo-page">
        <section className="seo-hero">
          <div className="container">
            <div className="seo-hero-inner">
              <p className="hero-announcement">{page.eyebrow}</p>
              <h1 className="heading-hero">{page.heroTitle}</h1>
              <p className="body-large seo-hero-copy">{page.intro}</p>
              <div className="hero-cta-group">
                <Link to="/#contact" className="btn-primary hover-scale">
                  Demander un devis
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/#portfolio" className="btn-secondary hover-scale">
                  Voir nos realisations
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container seo-content-grid">
            <div className="seo-panel">
              <h2 className="heading-2">Ce que nous mettons en place</h2>
              <ul className="seo-check-list">
                {page.bullets.map((bullet) => (
                  <li key={bullet}>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="seo-panel">
              {page.sections.map((section) => (
                <article key={section.title} className="seo-text-block">
                  <h2 className="heading-2">{section.title}</h2>
                  <p className="body-medium">{section.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">Services complementaires</h2>
              <p className="body-large">
                Un site performant combine souvent conception, contenu, technique
                et suivi dans la duree.
              </p>
            </div>
            <div className="seo-related-grid">
              {relatedPages.map((relatedPage) => (
                <Link
                  className="seo-related-link"
                  key={relatedPage.slug}
                  to={`/${relatedPage.slug}`}
                >
                  <span>{relatedPage.eyebrow}</span>
                  <strong>{relatedPage.title}</strong>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section alt">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">Questions frequentes</h2>
            </div>
            <div className="seo-faq-grid">
              {page.faq.map((item) => (
                <article className="seo-faq-item" key={item.question}>
                  <h3 className="heading-3">{item.question}</h3>
                  <p className="body-medium">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
